import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Card,
  CardContent,
  Chip,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  Area,
  AreaChart
} from 'recharts';
import { useTranslation } from 'react-i18next';
import { ConversationAnalyticsReport } from '../../types/reports';

interface ConversationAnalyticsChartProps {
  data: ConversationAnalyticsReport;
}

const ConversationAnalyticsChart: React.FC<ConversationAnalyticsChartProps> = ({ data }) => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

  const getDateLocale = () => {
    const lang = i18n.language;
    if (lang === 'pt-BR') return 'pt-BR';
    if (lang === 'es-ES') return 'es-ES';
    return 'en-US';
  };

  const dailyData = data.dailyStats.map(stat => ({
    ...stat,
    date: new Date(stat.date).toLocaleDateString(getDateLocale(), { 
      day: '2-digit', 
      month: '2-digit' 
    })
  }));

  const hourlyData = data.hourlyActivity.map(stat => ({
    ...stat,
    time: `${stat.hour}:00`
  }));

  const agentData = data.agentStats.map(stat => ({
    ...stat,
    agentName: stat.agentName.length > 15 
      ? stat.agentName.substring(0, 15) + '...' 
      : stat.agentName
  }));

  const statusData = [
    { name: t('pages.reports.charts.conversationAnalytics.active'), value: data.activeConversations, color: theme.palette.primary.main },
    { name: t('pages.reports.charts.conversationAnalytics.completed'), value: data.completedConversations, color: theme.palette.success.main },
    { name: t('pages.reports.charts.conversationAnalytics.unattended'), value: data.unattendedConversations, color: theme.palette.error.main }
  ];

  return (
    <Box sx={{ 
      width: '100%', 
      maxWidth: '100%', 
      overflow: 'hidden',
      px: isMobile ? 0 : 0
    }}>
      {/* Summary Cards */}
      <Box sx={{ 
        display: 'flex', 
        flexWrap: 'wrap', 
        gap: isMobile ? 1 : 3, 
        mb: isMobile ? 2 : 4,
        justifyContent: isMobile ? 'center' : 'flex-start'
      }}>
        <Box sx={{ 
          flex: isMobile ? '1 1 45%' : '1 1 200px', 
          minWidth: isMobile ? '45%' : '200px',
          maxWidth: isMobile ? '45%' : 'none'
        }}>
          <Card sx={{ height: isMobile ? '80px' : 'auto' }}>
            <CardContent sx={{ p: isMobile ? 1 : 2 }}>
              <Typography color="textSecondary" gutterBottom variant={isMobile ? "caption" : "body2"}>
{t('pages.reports.charts.conversationAnalytics.totalConversations')}
              </Typography>
              <Typography variant={isMobile ? "h6" : "h4"} sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                {data.totalConversations}
              </Typography>
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ 
          flex: isMobile ? '1 1 45%' : '1 1 200px', 
          minWidth: isMobile ? '45%' : '200px',
          maxWidth: isMobile ? '45%' : 'none'
        }}>
          <Card sx={{ height: isMobile ? '80px' : 'auto' }}>
            <CardContent sx={{ p: isMobile ? 1 : 2 }}>
              <Typography color="textSecondary" gutterBottom variant={isMobile ? "caption" : "body2"}>
{t('pages.reports.charts.conversationAnalytics.activeConversations')}
              </Typography>
              <Typography variant={isMobile ? "h6" : "h4"} sx={{ fontWeight: 'bold', color: 'success.main' }}>
                {data.activeConversations}
              </Typography>
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ 
          flex: isMobile ? '1 1 45%' : '1 1 200px', 
          minWidth: isMobile ? '45%' : '200px',
          maxWidth: isMobile ? '45%' : 'none'
        }}>
          <Card sx={{ height: isMobile ? '80px' : 'auto' }}>
            <CardContent sx={{ p: isMobile ? 1 : 2 }}>
              <Typography color="textSecondary" gutterBottom variant={isMobile ? "caption" : "body2"}>
{t('pages.reports.charts.conversationAnalytics.completionRate')}
              </Typography>
              <Typography variant={isMobile ? "h6" : "h4"} sx={{ fontWeight: 'bold', color: 'warning.main' }}>
                {((data.completedConversations / data.totalConversations) * 100).toFixed(1)}%
              </Typography>
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ 
          flex: isMobile ? '1 1 45%' : '1 1 200px', 
          minWidth: isMobile ? '45%' : '200px',
          maxWidth: isMobile ? '45%' : 'none'
        }}>
          <Card sx={{ height: isMobile ? '80px' : 'auto' }}>
            <CardContent sx={{ p: isMobile ? 1 : 2 }}>
              <Typography color="textSecondary" gutterBottom variant={isMobile ? "caption" : "body2"}>
{t('pages.reports.charts.conversationAnalytics.averageMessages')}
              </Typography>
              <Typography variant={isMobile ? "h6" : "h4"} sx={{ fontWeight: 'bold', color: 'info.main' }}>
                {data.averageMessagesPerConversation.toFixed(1)}
              </Typography>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Charts */}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        gap: isMobile ? 2 : 3,
        width: '100%',
        maxWidth: '100%',
        overflow: 'hidden'
      }}>
        {/* Daily Conversations Chart */}
        <Box sx={{ 
          display: 'flex', 
          flexDirection: isMobile ? 'column' : 'row',
          flexWrap: isMobile ? 'nowrap' : 'wrap', 
          gap: isMobile ? 2 : 3,
          width: '100%',
          maxWidth: '100%'
        }}>
          <Box sx={{ 
            flex: isMobile ? '1 1 100%' : '1 1 600px', 
            minWidth: isMobile ? '100%' : '600px',
            width: '100%',
            maxWidth: '100%'
          }}>
            <Paper sx={{ 
              p: isMobile ? 1 : 3,
              width: '100%',
              maxWidth: '100%',
              overflow: 'hidden'
            }}>
              <Typography variant={isMobile ? "subtitle1" : "h6"} gutterBottom sx={{ fontWeight: 'bold' }}>
                {t('pages.reports.charts.conversationAnalytics.conversationsPerDay')}
              </Typography>
              <ResponsiveContainer width="100%" height={isMobile ? 280 : 300}>
                <AreaChart data={dailyData} margin={{ top: 5, right: 30, left: 20, bottom: 60 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Area
                    type="monotone"
                    dataKey="totalConversations"
                    stackId="1"
                    stroke={theme.palette.primary.main}
                    fill={theme.palette.primary.main}
                    fillOpacity={0.6}
                    name={t('pages.reports.charts.conversationAnalytics.totalConversationsLabel')}
                  />
                  <Area
                    type="monotone"
                    dataKey="activeConversations"
                    stackId="2"
                    stroke={theme.palette.success.main}
                    fill={theme.palette.success.main}
                    fillOpacity={0.6}
                    name={t('pages.reports.charts.conversationAnalytics.activeConversationsLabel')}
                  />
                  <Area
                    type="monotone"
                    dataKey="completedConversations"
                    stackId="3"
                    stroke={theme.palette.warning.main}
                    fill={theme.palette.warning.main}
                    fillOpacity={0.6}
                    name={t('pages.reports.charts.conversationAnalytics.completedConversations')}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </Paper>
          </Box>

          {/* Status Distribution */}
          <Box sx={{ 
            flex: isMobile ? '1 1 100%' : '1 1 300px', 
            minWidth: isMobile ? '100%' : '300px',
            width: '100%',
            maxWidth: '100%'
          }}>
            <Paper sx={{ 
              p: isMobile ? 1 : 3,
              width: '100%',
              maxWidth: '100%',
              overflow: 'hidden'
            }}>
              <Typography variant={isMobile ? "subtitle1" : "h6"} gutterBottom sx={{ fontWeight: 'bold' }}>
                {t('pages.reports.charts.conversationAnalytics.conversationStatus')}
              </Typography>
              <ResponsiveContainer width="100%" height={isMobile ? 200 : 300}>
                <PieChart>
                  <Pie
                    data={statusData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, value }) => `${name}: ${value}`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {statusData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </Paper>
          </Box>
        </Box>

        {/* Hourly Activity */}
        <Box sx={{ 
          display: 'flex', 
          flexDirection: isMobile ? 'column' : 'row',
          flexWrap: isMobile ? 'nowrap' : 'wrap', 
          gap: isMobile ? 2 : 3,
          width: '100%',
          maxWidth: '100%'
        }}>
          <Box sx={{ 
            flex: isMobile ? '1 1 100%' : '1 1 600px', 
            minWidth: isMobile ? '100%' : '600px',
            width: '100%',
            maxWidth: '100%'
          }}>
            <Paper sx={{ 
              p: isMobile ? 1 : 3,
              width: '100%',
              maxWidth: '100%',
              overflow: 'hidden'
            }}>
              <Typography variant={isMobile ? "subtitle1" : "h6"} gutterBottom sx={{ fontWeight: 'bold' }}>
                {t('pages.reports.charts.conversationAnalytics.hourlyActivity')}
              </Typography>
              <ResponsiveContainer width="100%" height={isMobile ? 200 : 300}>
                <BarChart data={hourlyData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="time" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="conversations" fill={theme.palette.primary.main} name={t('pages.reports.charts.conversationAnalytics.conversationsLabel')} />
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          </Box>

          {/* Status Breakdown */}
          <Box sx={{ 
            flex: isMobile ? '1 1 100%' : '1 1 300px', 
            minWidth: isMobile ? '100%' : '300px',
            width: '100%',
            maxWidth: '100%'
          }}>
            <Paper sx={{ 
              p: isMobile ? 1 : 3,
              width: '100%',
              maxWidth: '100%',
              overflow: 'hidden'
            }}>
              <Typography variant={isMobile ? "subtitle1" : "h6"} gutterBottom sx={{ fontWeight: 'bold' }}>
                {t('pages.reports.charts.conversationAnalytics.statusBreakdown')}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {statusData.map((status, index) => (
                  <Box key={index} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="body2">{status.name}</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        {status.value}
                      </Typography>
                      <Chip
                        label={`${((status.value / data.totalConversations) * 100).toFixed(1)}%`}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    </Box>
                  </Box>
                ))}
              </Box>
            </Paper>
          </Box>
        </Box>

        {/* Agent Performance */}
        <Box sx={{ 
          display: 'flex', 
          flexDirection: isMobile ? 'column' : 'row',
          flexWrap: isMobile ? 'nowrap' : 'wrap', 
          gap: isMobile ? 2 : 3,
          width: '100%',
          maxWidth: '100%'
        }}>
          <Box sx={{ 
            flex: isMobile ? '1 1 100%' : '1 1 600px', 
            minWidth: isMobile ? '100%' : '600px',
            width: '100%',
            maxWidth: '100%'
          }}>
            <Paper sx={{ 
              p: isMobile ? 1 : 3,
              width: '100%',
              maxWidth: '100%',
              overflow: 'hidden'
            }}>
              <Typography variant={isMobile ? "subtitle1" : "h6"} gutterBottom sx={{ fontWeight: 'bold' }}>
                {t('pages.reports.charts.conversationAnalytics.agentPerformance')}
              </Typography>
              <ResponsiveContainer width="100%" height={isMobile ? 200 : 300}>
                <BarChart data={agentData} layout="horizontal">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis dataKey="agentName" type="category" width={100} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="totalConversations" fill={theme.palette.primary.main} name={t('pages.reports.charts.conversationAnalytics.totalConversationsBar')} />
                  <Bar dataKey="completedConversations" fill={theme.palette.success.main} name={t('pages.reports.charts.conversationAnalytics.completedConversationsBar')} />
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          </Box>

          {/* Average Messages Chart */}
          <Box sx={{ 
            flex: isMobile ? '1 1 100%' : '1 1 300px', 
            minWidth: isMobile ? '100%' : '300px',
            width: '100%',
            maxWidth: '100%'
          }}>
            <Paper sx={{ 
              p: isMobile ? 1 : 3,
              width: '100%',
              maxWidth: '100%',
              overflow: 'hidden'
            }}>
              <Typography variant={isMobile ? "subtitle1" : "h6"} gutterBottom sx={{ fontWeight: 'bold' }}>
                {t('pages.reports.charts.conversationAnalytics.averageMessagesPerAgent')}
              </Typography>
              <ResponsiveContainer width="100%" height={isMobile ? 200 : 300}>
                <BarChart data={agentData} layout="horizontal">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis dataKey="agentName" type="category" width={100} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="averageMessages" fill={theme.palette.info.main} name={t('pages.reports.charts.conversationAnalytics.averageMessagesLabel')} />
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          </Box>
        </Box>

        {/* Agent Details Table */}
        <Box sx={{ 
          width: '100%',
          maxWidth: '100%',
          overflow: 'hidden'
        }}>
          <Paper sx={{ 
            p: isMobile ? 1 : 3,
            width: '100%',
            maxWidth: '100%',
            overflow: 'hidden'
          }}>
            <Typography variant={isMobile ? "subtitle1" : "h6"} gutterBottom sx={{ fontWeight: 'bold' }}>
              {t('pages.reports.charts.conversationAnalytics.agentDetails')}
            </Typography>
            <Box sx={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #e0e0e0' }}>
                    <th style={{ padding: '12px', textAlign: 'left' }}>{t('pages.reports.charts.conversationAnalytics.agent')}</th>
                    <th style={{ padding: '12px', textAlign: 'left' }}>{t('pages.reports.charts.conversationAnalytics.totalConversations')}</th>
                    <th style={{ padding: '12px', textAlign: 'left' }}>{t('pages.reports.charts.conversationAnalytics.completed')}</th>
                    <th style={{ padding: '12px', textAlign: 'left' }}>{t('pages.reports.charts.conversationAnalytics.completionRateLabel')}</th>
                    <th style={{ padding: '12px', textAlign: 'left' }}>{t('pages.reports.charts.conversationAnalytics.averageMessagesPerConversation')}</th>
                  </tr>
                </thead>
                <tbody>
                  {agentData.map((agent, index) => (
                    <tr key={index} style={{ borderBottom: '1px solid #f0f0f0' }}>
                      <td style={{ padding: '12px' }}>{agent.agentName}</td>
                      <td style={{ padding: '12px' }}>{agent.totalConversations}</td>
                      <td style={{ padding: '12px' }}>{agent.completedConversations}</td>
                      <td style={{ padding: '12px' }}>{agent.completionRate.toFixed(1)}%</td>
                      <td style={{ padding: '12px' }}>{agent.averageMessages.toFixed(1)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Box>
          </Paper>
        </Box>
      </Box>
    </Box>
  );
};

export default ConversationAnalyticsChart;